"use client";
import React, { createContext, useContext, useCallback, useState } from "react";
import { BASE_URL } from "../config/config";

const SubscriptionPlanContext = createContext();

export const SubscriptionPlanProvider = ({ children }) => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const token = localStorage.getItem("token");

  // 🔹 FETCH PLANS
  const fetchPlans = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await fetch(`${BASE_URL}/subscription-plans`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await res.json();

      if (res.ok && data.status === 200) {
        setPlans(data.data || []);
      } else {
        setError(data.message || "Failed to fetch plans");
      }
    } catch {
      setError("Network error");
    } finally {
      setLoading(false);
    }
  }, [token]);

  // 🔹 ADD PLAN
  const addPlan = async (planData) => {
    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");

      const payload = {
        ...planData,
        create_audit_id: user.id,
      };

      const res = await fetch(`${BASE_URL}/subscription-plans`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (res.ok && (data.status === 200 || data.status === 201)) {
        setPlans((prev) => [data.data, ...prev]);
        return true;
      } else {
        alert(data.message || "Add plan failed");
        return false;
      }
    } catch (err) {
      console.error(err);
      alert("Network error");
      return false;
    }
  };

  // 🔹 UPDATE PLAN
  const updatePlan = async (id, payload) => {
    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      payload.update_audit_id = user.id;

      const res = await fetch(`${BASE_URL}/subscription-plans/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (res.ok && data.status === 200) {
        setPlans((prev) =>
          prev.map((p) => (p.id === id ? { ...p, ...payload } : p))
        );
        return true;
      } else {
        alert(data.message || "Update plan failed");
      }
    } catch {
      alert("Network error");
    }
  };

  // 🔹 DELETE PLAN
  const deletePlan = async (id) => {
    if (!window.confirm("Delete this plan?")) return;
    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");

      const res = await fetch(`${BASE_URL}/subscription-plans/${id}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          delete_audit_id: user.id,
        }),
      });

      const data = await res.json();

      if (res.ok && data.status === 200) {
        setPlans((prev) => prev.filter((p) => p.id !== id));
      } else {
        alert(data.message || "Delete failed");
      }
    } catch {
      alert("Delete failed");
    }
  };

  // 🔹 TOGGLE STATUS
  const togglePlanStatus = async (id, currentStatus) => {
    const newStatus = currentStatus === "Active" ? "Inactive" : "Active";
    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");

      const res = await fetch(`${BASE_URL}/subscription-plans/${id}/status`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          status: newStatus,
          update_audit_id: user.id,
        }),
      });

      const data = await res.json();

      if (res.ok && data.status === 200) {
        setPlans((prev) =>
          prev.map((p) => (p.id === id ? { ...p, status: newStatus } : p))
        );
      } else {
        alert(data.message || "Status update failed");
      }
    } catch {
      alert("Status update failed");
    }
  };

  return (
    <SubscriptionPlanContext.Provider
      value={{
        plans,
        loading,
        error,
        fetchPlans,
        addPlan,
        updatePlan,
        deletePlan,
        togglePlanStatus,
      }}
    >
      {children}
    </SubscriptionPlanContext.Provider>
  );
};

export const useSubscriptionPlans = () => useContext(SubscriptionPlanContext);
